import { Model, RailsFormat } from "./model";
import { Address } from "./address";
import { Phone } from "./phone";
import { Note } from "./note";
import { CreditCard } from "./credit-card";
import { Document } from "./document";
import { Discount } from "./discount";
import { AlternateEmail } from "./alternate-email";
import { Enrolment } from "./enrolment";
import { MatrixClass } from "./matrix-class";
import { Absence } from "./absence";
import { Utilities } from "@helpers/utilities";
import { ModifiedClass } from "./modified-class";
import { Course } from "./course";
import { TutorAllocation } from "./tutor-allocation";
import { WorkshopAbsence } from "./workshop-absence";

export class Contact extends Model implements RailsFormat {

  constructor(
    public id?: number,
    public tutorAllocationId?: number,
    public guardianId?: number,
    public firstName: string = "",
    public lastName: string = "",
    public fullName: string = "",
    public email: string = "",
    public gender: number = 0,
    public contactType: number = 0,
    public school: string = "",
    public grade: number = null,
    public joiningGrade: number = 0,
    public salutation: string = "",
    public dob: Date = null,
    public medicalCondition: string = "",
    public promoCode: string = "",
    public marketingSource: string = "",
    public newsletterSubscription: boolean = false,
    public instalments: number = 0,
    public employmentStart: Date = null,

    public updatedAt: Date = null,
    public createdAt: Date = null,

    public avatar: string = "",
    public avatarFileName: string = "",
    public avatarUrl: string = "",

    public guardian: Contact = null,
    public students: Contact[] = [],
    public creditCard: CreditCard = null,

    public matrixClasses: MatrixClass[] = [],
    public addresses: Address[] = [],
    public phones: Phone[] = [],
    public alternateEmails: AlternateEmail[] = [],
    public enrolments: Enrolment[] = [],
    public studentCourses: Course[] = [],
    public tutorCourses: Course[] = [],
    public tutorAllocation: TutorAllocation = null,
    public absences: Absence[] = [],
    public workshopAbsences: WorkshopAbsence[] = [],
    public workshopAbsencesReplacements: WorkshopAbsence[] = [],
    public modifiedClasses: ModifiedClass[] = [],
    public notes: Note[] = [],
    public documents: Document[] = [],
    public discounts: Discount[] = [],
  ) {
    super()
  }

  static clone(obj: any) {
    const contact: Contact = new this()

    if (obj.id) { contact.id = obj.id }
    if (obj.guardianId) { contact.guardianId = obj.guardianId }
    if (obj.tutorAllocationId) { contact.tutorAllocationId = obj.tutorAllocationId }
    if (obj.firstName) { contact.firstName = obj.firstName }
    if (obj.lastName) { contact.lastName = obj.lastName }
    if (obj.fullName) { contact.fullName = obj.fullName }
    if (obj.email) { contact.email = obj.email }
    if (obj.gender) { contact.gender = obj.gender }
    if (obj.contactType) { contact.contactType = obj.contactType }
    if (obj.school) { contact.school = obj.school }
    if (obj.grade) { contact.grade = obj.grade }
    if (obj.joiningGrade) { contact.joiningGrade = obj.joiningGrade }
    if (obj.salutation) { contact.salutation = obj.salutation }
    if (obj.dob) { contact.dob = Utilities.makeDate(obj.dob) }
    if (obj.medicalCondition) { contact.medicalCondition = obj.medicalCondition }
    if (obj.promoCode) { contact.promoCode = obj.promoCode }
    if (obj.marketingSource) { contact.marketingSource = obj.marketingSource }
    if (obj.newsletterSubscription) { contact.newsletterSubscription = obj.newsletterSubscription }
    if (obj.instalments) { contact.instalments = obj.instalments }
    if (obj.employmentStart) { contact.employmentStart = Utilities.makeDate(obj.employmentStart) }

    if (obj.tutorAllocation) { contact.tutorAllocation = TutorAllocation.clone(obj.tutorAllocation) }

    if (obj.updatedAt) { contact.updatedAt = obj.updatedAt }
    if (obj.createdAt) { contact.createdAt = obj.createdAt }

    if (obj.avatar) { contact.avatar = obj.avatar }
    if (obj.avatarFileName) { contact.avatarFileName = obj.avatarFileName }
    if (obj.avatarUrl) { contact.avatarUrl = obj.avatarUrl }

    if (obj.guardian) { contact.guardian = Contact.clone(obj.guardian) }
    if (obj.creditCard) { contact.creditCard = CreditCard.clone(obj.creditCard) }
    if (obj.students && obj.students.length > 0) {
      obj.students.forEach((s: any) => {
        contact.students.push(Contact.clone(s))
      })
    }

    if (obj.addresses && obj.addresses.length > 0) {
      obj.addresses.forEach((a: any) => {
        contact.addresses.push(Address.clone(a))
      })
    }

    if (obj.phones && obj.phones.length > 0) {
      obj.phones.forEach((p: any) => {
        contact.phones.push(Phone.clone(p))
      })
    }

    if (obj.alternateEmails && obj.alternateEmails.length > 0) {
      obj.alternateEmails.forEach((e: any) => {
        contact.alternateEmails.push(AlternateEmail.clone(e))
      })
    }

    if (obj.enrolments && obj.enrolments.length > 0) {
      obj.enrolments.forEach((e: any) => {
        contact.enrolments.push(Enrolment.clone(e))
      })
    }

    if (obj.studentCourses && obj.studentCourses.length > 0) {
      obj.studentCourses.forEach((c: any) => {
        contact.studentCourses.push(Course.clone(c))
      })
    }

    if (obj.tutorCourses && obj.tutorCourses.length > 0) {
      obj.tutorCourses.forEach((c: any) => {
        contact.tutorCourses.push(Course.clone(c))
      })
    }

    if (obj.matrixClasses && obj.matrixClasses.length > 0) {
      obj.matrixClasses.forEach((c: any) => {
        contact.matrixClasses.push(MatrixClass.clone(c))
      })
    }

    if (obj.absences && obj.absences.length > 0) {
      obj.absences.forEach((a: any) => {
        contact.absences.push(Absence.clone(a))
      })
    }

    if (obj.workshopAbsences && obj.workshopAbsences.length > 0) {
      obj.workshopAbsences.forEach((wa: any) => {
        contact.workshopAbsences.push(WorkshopAbsence.clone(wa))
      })
    }

    if (obj.workshopAbsencesReplacements && obj.workshopAbsencesReplacements.length > 0) {
      obj.workshopAbsencesReplacements.forEach((wa: any) => {
        contact.workshopAbsencesReplacements.push(WorkshopAbsence.clone(wa))
      })
    }

    if (obj.modifiedClasses && obj.modifiedClasses.length > 0) {
      obj.modifiedClasses.forEach((modC: any) => {
        contact.modifiedClasses.push(ModifiedClass.clone(modC))
      })
    }

    if (obj.notes && obj.notes.length > 0) {
      obj.notes.forEach((n: any) => {
        contact.notes.push(Note.clone(n))
      })
    }

    if (obj.documents && obj.documents.length > 0) {
      obj.documents.forEach((d: any) => {
        contact.documents.push(Document.clone(d))
      })
    }

    if (obj.discounts && obj.discounts.length > 0) {
      obj.discounts.forEach((d: any) => {
        contact.discounts.push(Discount.clone(d))
      })
    }

    if (obj.errors && Object.keys(obj.errors).length > 0) {
      contact.errors = Model.getJsonApiErrors(obj.errors)
    }

    return contact
  }

  serializeForRails(): any {
    const response: any = Object.assign({}, this)

    if (this.id === 0) { response.id = null }

    if (response.addresses && response.addresses.length > 0) {
      response.addressesAttributes = response.addresses
    }

    if (response.phones && response.phones.length > 0) {
      response.phonesAttributes = response.phones
    }

    if (response.alternateEmails && response.alternateEmails.length > 0) {
      response.alternateEmailsAttributes = response.alternateEmails
    }

    return response
  }

}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/models/contact.ts